// @file Surface language panel store
import { getHyphenatedCurrentLocale } from '@@/bits/current_locale'
import { captureFetchException } from '@@/bits/error_tracker'
import { __, p__ } from '@@/bits/intl'
import { availableLocales } from '@@/bits/locale'
import { SnackbarNotificationType, useGlobalSnackbarStore } from '@@/pinia/global_snackbar'
import { useSurfaceStore } from '@@/pinia/surface'
import { useCommentsStore } from '@@/pinia/surface_comments'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useRealtimeStore } from '@@/pinia/surface_realtime'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import { WallLanguageDetectionApi } from '@@/surface/api/wall_language_detection'
import { WallTranslationApi } from '@@/surface/api/wall_translation'
import type { TranslatedWall } from '@@/types'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export const ORIGINAL_LANGUAGE_KEY = 'original'

export const useSurfaceTranslationsStore = defineStore('surfaceTranslationsStore', () => {
  const surfaceStore = useSurfaceStore()
  const surfaceSectionsStore = useSurfaceSectionsStore()
  const surfacePostsStore = useSurfacePostsStore()
  const surfaceCommentsStore = useCommentsStore()
  const globalSnackbarStore = useGlobalSnackbarStore()
  const surfaceRealtimeStore = useRealtimeStore()

  const xSurfaceLanguagePanel = ref<boolean>(false)
  const currentLanguage = ref<string>(ORIGINAL_LANGUAGE_KEY)
  const unsavedLanguage = ref<string>(currentLanguage.value)
  const detectedLanguage = ref<string>('')
  const isTranslatingPadletFromPanel = ref<boolean>(false)
  const isTranslatingPadletFromBanner = ref<boolean>(false)
  const isDetectingLanguage = ref<boolean>(false)

  const isViewingTranslatedPadlet = computed<boolean>(() => currentLanguage.value !== ORIGINAL_LANGUAGE_KEY)

  const normalizedLocale = computed(() => (locale: string): string => {
    // Special case for Filipino because the API we use uses 'fil' instead of 'tl' which is what we use
    if (locale === 'fil') return 'tl'
    return locale === 'zh' ? getHyphenatedCurrentLocale() : locale
  })

  // Presentable name for current language with custom logic for original language
  const currentLanguageDisplayName = computed<string>(() => {
    if (currentLanguage.value === ORIGINAL_LANGUAGE_KEY) {
      return p__(
        'This text appears in the language selector panel. It shows the original language of the content. localeName is the name of the language that we have detected the content to be in.',
        'Original ({localeName})',
        { localeName: detectedLanguageDisplayName.value },
      )
    }
    return availableLocales.find((locale) => locale.html === normalizedLocale.value(currentLanguage.value))?.name ?? ''
  })

  const detectedLanguageDisplayName = computed<string>(() => {
    if (detectedLanguage.value === 'mixed') {
      return p__('Indicates that the text contains multiple languages mixed together', 'Mixed')
    } else if (detectedLanguage.value === 'unknown') {
      return p__('Indicates that the text is in a language that we could not detect', 'Unknown')
    }
    return availableLocales.find((locale) => locale.html === normalizedLocale.value(detectedLanguage.value))?.name ?? ''
  })

  const showLanguagePanel = (): void => {
    xSurfaceLanguagePanel.value = true
  }

  const closeLanguagePanel = (): void => {
    xSurfaceLanguagePanel.value = false
    // Reset unsaved language to current language
    unsavedLanguage.value = currentLanguage.value
  }

  const handleRealtimeConnection = (selectedLanguage: string): void => {
    if (selectedLanguage !== ORIGINAL_LANGUAGE_KEY) {
      surfaceRealtimeStore.disconnectBrahms()
    } else {
      surfaceRealtimeStore.createNewBrahmsConnectionForUser()
    }
  }

  const handleWallUpdates = (translatedWall: TranslatedWall): void => {
    surfaceStore.updateWall({ title: translatedWall.title, description: translatedWall.description })
    surfaceSectionsStore.updateSectionEntitiesWithTranslatedTitles(translatedWall.sections)
    surfacePostsStore.updatePostsWithTranslations(translatedWall.wishes)
    surfaceCommentsStore.updateCommentsWithTranslatedBody(translatedWall.comments)
  }

  async function handleTranslation(selectedLanguage: string, source: 'panel' | 'banner'): Promise<void> {
    try {
      handleRealtimeConnection(selectedLanguage)

      if (source === 'panel') {
        isTranslatingPadletFromPanel.value = true
      } else {
        isTranslatingPadletFromBanner.value = true
      }

      const translatedWall = await WallTranslationApi.translate(surfaceStore.wallId, selectedLanguage)
      handleWallUpdates(translatedWall)
      currentLanguage.value = selectedLanguage
    } catch (e) {
      const translationFailedSnackbarUid = globalSnackbarStore.setSnackbar({
        message: __('Padlet could not be translated'),
        notificationType: SnackbarNotificationType.error,
        timeout: 5000,
        actionText: __('Retry'),
        actionTextActions: [
          async () => {
            globalSnackbarStore.removeSnackbar(translationFailedSnackbarUid)
            await handleTranslation(selectedLanguage, source)
          },
        ],
      })
      captureFetchException(e, { source: 'SurfaceTranslatePadletToLanguage' })
    } finally {
      isTranslatingPadletFromPanel.value = false
      isTranslatingPadletFromBanner.value = false
      closeLanguagePanel()
    }
  }

  async function translatePadletFromLanguagePanel(selectedLanguage: string): Promise<void> {
    await handleTranslation(selectedLanguage, 'panel')
  }

  async function translatePadletFromBanner(selectedLanguage: string): Promise<void> {
    await handleTranslation(selectedLanguage, 'banner')
  }

  async function detectLanguage(): Promise<void> {
    try {
      isDetectingLanguage.value = true
      const data = await WallLanguageDetectionApi.detectLanguage(surfaceStore.wallId)
      detectedLanguage.value = data.language
    } catch (e) {
      captureFetchException(e, { source: 'SurfaceLanguageDetection' })
    } finally {
      isDetectingLanguage.value = false
    }
  }

  const hasUnsavedLanguageChanges = computed(() => unsavedLanguage.value !== currentLanguage.value)

  const setUnsavedLanguage = (language: string): void => {
    unsavedLanguage.value = language
  }

  return {
    isDetectingLanguage,
    xSurfaceLanguagePanel,
    currentLanguage,
    detectedLanguage,
    unsavedLanguage,
    isTranslatingPadletFromPanel,
    isTranslatingPadletFromBanner,
    showLanguagePanel,
    closeLanguagePanel,
    translatePadletFromLanguagePanel,
    translatePadletFromBanner,
    detectLanguage,
    hasUnsavedLanguageChanges,
    setUnsavedLanguage,
    currentLanguageDisplayName,
    isViewingTranslatedPadlet,
    normalizedLocale,
  }
})
